import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, Rect, Textbox, FabricImage, Circle, Triangle } from 'fabric';
import storage from '../../../lib/storage';
import { removeBgService } from '../../../services';
import { buildFabricObjectFromLayer, getCanvasPayloadsFromStructure } from '../../../engine/CreativeCanvasEngine';
import manualEditorService from '../services/manualEditorService';

const CANVAS_SIZE = 1080;

const newLayerId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `layer-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getThemeColors = () => {
  const root = window.getComputedStyle(document.documentElement);
  return {
    bg: root.getPropertyValue('--surface-2')?.trim() || '#0f172a',
    fg: root.getPropertyValue('--text-primary')?.trim() || '#ffffff',
    accent: root.getPropertyValue('--accent-primary')?.trim() || '#6366f1',
  };
};

const normalizeObject = (object) => {
  if (!object.get('mpObjectId')) object.set('mpObjectId', newLayerId());
  return object;
};

const firstCanvasDataFromPayload = (designPayload) => {
  const payloads = getCanvasPayloadsFromStructure(designPayload);
  if (!payloads.length) return null;
  return payloads[0].canvasData || null;
};

export default function useManualEditor({
  initialDesignId = null,
  initialCanvasJson = null,
  initialImageUrl = null,
  initialDesignPayload = null,
}) {
  const canvasElementRef = useRef(null);
  const canvasRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const isHydratingRef = useRef(false);
  const isSavingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const designIdRef = useRef(initialDesignId || null);

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [designId, setDesignId] = useState(initialDesignId || null);
  const [designTitle, setDesignTitle] = useState('Arte manual');
  const [layers, setLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);
  const [activeObjectType, setActiveObjectType] = useState('');
  const [videoSourceUrl, setVideoSourceUrl] = useState('');
  const [opacity, setOpacity] = useState(100);
  const [textStyle, setTextStyle] = useState({ family: 'Inter', weight: '400', size: 52, spacing: 0, preview_url: '' });

  const themeColors = useMemo(() => getThemeColors(), []);

  const loadFontCss = useCallback((previewUrl) => {
    if (!previewUrl) return;
    const key = `manual-font-${encodeURIComponent(previewUrl)}`;
    if (document.querySelector(`link[data-mp-font="${key}"]`)) return;

    const link = document.createElement('link');
    link.href = previewUrl;
    link.rel = 'stylesheet';
    link.dataset.mpFont = key;
    document.head.appendChild(link);
  }, []);

  const refreshLayers = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const mapped = canvas
      .getObjects()
      .filter((object) => object.get('selectable') !== false)
      .map((object, index) => {
        normalizeObject(object);
        const labelByType = {
          textbox: 'Texto',
          rect: 'Forma',
          circle: 'Forma',
          triangle: 'Forma',
          image: object.get('mpMediaType') === 'video' ? 'Vídeo' : 'Imagem',
        };

        return {
          id: object.get('mpObjectId'),
          name: `${labelByType[object.type] || 'Camada'} ${index + 1}`,
          type: object.type,
        };
      });

    setLayers(mapped.reverse());

    const active = canvas.getActiveObject();
    setActiveLayerId(active?.get('mpObjectId') || null);
    setActiveObjectType(active?.type || '');
    setOpacity(Math.round(((active?.opacity ?? 1) * 100)));

    if (active && (active.type === 'textbox' || active.type === 'text')) {
      setTextStyle((prev) => ({
        ...prev,
        family: active.fontFamily || prev.family,
        weight: String(active.fontWeight || prev.weight),
        size: Number(active.fontSize || prev.size),
        spacing: Number(active.charSpacing || prev.spacing),
      }));
    }
  }, []);

  const addBackground = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const hasBackground = canvas.getObjects().some((object) => object.get('mpRole') === 'background');
    if (hasBackground) return;

    const background = new Rect({
      left: 0,
      top: 0,
      width: CANVAS_SIZE,
      height: CANVAS_SIZE,
      fill: themeColors.bg,
      selectable: false,
      evented: false,
    });
    background.set('mpRole', 'background');
    canvas.add(background);
    if (typeof canvas.sendObjectToBack === 'function') canvas.sendObjectToBack(background);
    canvas.requestRenderAll();
  }, [themeColors.bg]);

  const waitForCanvasImages = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const imageObjects = canvas.getObjects().filter((object) => object.type === 'image');
    await Promise.all(imageObjects.map((object) => new Promise((resolve) => {
      const element = object.getElement?.() || object._element;
      if (!element || element.complete) {
        resolve();
        return;
      }
      element.addEventListener('load', resolve, { once: true });
      element.addEventListener('error', resolve, { once: true });
    })));
  }, []);

  const ensureDesignExists = useCallback(async () => {
    if (designIdRef.current) return designIdRef.current;

    const token = storage.get('authToken');
    const sessionId = storage.get('guest_session_id');
    const created = await manualEditorService.createManualDesign({
      prompt: designTitle || 'Arte manual',
      media_type: 'photo',
      ...(token ? {} : { session_id: sessionId || '' }),
    });

    if (!created?.success || !created?.design?.id) {
      throw new Error('Falha ao criar design manual');
    }

    const id = created.design.id;
    designIdRef.current = id;
    setDesignId(id);
    return id;
  }, [designTitle]);

  const saveCanvas = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (isSavingRef.current) {
      pendingSaveRef.current = true;
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setError('');

    try {
      if (document.fonts?.ready) await document.fonts.ready;
      await waitForCanvasImages();

      const id = await ensureDesignExists();
      const previewImage = canvas.toDataURL({ format: 'png', multiplier: 2 });
      const previewThumb = canvas.toDataURL({ format: 'png', multiplier: 0.4 });

      await manualEditorService.updateCanvas(id, canvas.toJSON(['mpObjectId', 'mpRole', 'mpMediaType', 'mpSourceUrl', 'src']), {
        previewImage,
        previewThumb,
        prompt: designTitle || 'Arte manual',
      });
    } catch {
      setError('Não foi possível salvar o design na biblioteca.');
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        saveCanvas();
      }
    }
  }, [designTitle, ensureDesignExists, waitForCanvasImages]);

  const scheduleAutoSave = useCallback(() => {
    if (isHydratingRef.current) return;
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      saveCanvas();
    }, 1200);
  }, [saveCanvas]);

  const loadCanvasJson = useCallback((canvasJson) => {
    const canvas = canvasRef.current;
    if (!canvas || !canvasJson) return;

    isHydratingRef.current = true;
    canvas.loadFromJSON(canvasJson, () => {
      canvas.getObjects().forEach((object) => normalizeObject(object));
      canvas.renderAll();
      refreshLayers();
      isHydratingRef.current = false;
    });
  }, [refreshLayers]);

  const loadCanvasPayloadIntoCanvas = useCallback(async (designPayload) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const canvasData = firstCanvasDataFromPayload(designPayload);
    if (!canvasData || !Array.isArray(canvasData.layers)) return;

    isHydratingRef.current = true;

    const removable = canvas.getObjects().filter((object) => object.get('mpRole') !== 'background');
    removable.forEach((object) => canvas.remove(object));

    for (const layer of canvasData.layers) {
      try {
        const object = await buildFabricObjectFromLayer(layer);
        if (!object) continue;
        normalizeObject(object);
        canvas.add(object);
      } catch {
        setError('Falha ao carregar uma camada do design no canvas.');
      }
    }

    canvas.requestRenderAll();
    refreshLayers();
    isHydratingRef.current = false;
  }, [refreshLayers]);

  useEffect(() => {
    if (!canvasElementRef.current) return;

    const canvas = new Canvas(canvasElementRef.current, {
      width: CANVAS_SIZE,
      height: CANVAS_SIZE,
      preserveObjectStacking: true,
      selection: true,
    });
    canvasRef.current = canvas;
    addBackground();

    const bindRefresh = () => {
      refreshLayers();
      scheduleAutoSave();
    };

    canvas.on('selection:created', refreshLayers);
    canvas.on('selection:updated', refreshLayers);
    canvas.on('selection:cleared', refreshLayers);
    canvas.on('object:added', bindRefresh);
    canvas.on('object:removed', bindRefresh);
    canvas.on('object:modified', bindRefresh);

    if (initialCanvasJson) {
      loadCanvasJson(initialCanvasJson);
    } else {
      const initialText = new Textbox('Criação Manual', {
        left: 90,
        top: 90,
        width: 620,
        fontSize: 72,
        fill: themeColors.fg,
        fontFamily: textStyle.family,
        fontWeight: textStyle.weight,
      });
      normalizeObject(initialText);
      canvas.add(initialText);
      refreshLayers();
    }

    setIsReady(true);

    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
      canvas.dispose();
      canvasRef.current = null;
    };
  }, [addBackground, initialCanvasJson, loadCanvasJson, refreshLayers, scheduleAutoSave, textStyle.family, textStyle.weight, themeColors.fg]);

  useEffect(() => {
    if (!initialDesignId || !canvasRef.current) return;

    const load = async () => {
      setIsLoading(true);
      setError('');
      isHydratingRef.current = true;
      try {
        const data = await manualEditorService.getDesign(initialDesignId);
        if (data?.success && data.design) {
          setDesignId(data.design.id);
          designIdRef.current = data.design.id;
          setDesignTitle(data.design.prompt || 'Arte manual');
          if (data.design.design_payload?.type === 'canvas-data') {
            await loadCanvasPayloadIntoCanvas(data.design.design_payload);
          } else if (data.design.canvas_json) {
            loadCanvasJson(data.design.canvas_json);
          }
        }
      } catch {
        setError('Não foi possível carregar a arte para edição.');
      } finally {
        isHydratingRef.current = false;
        setIsLoading(false);
      }
    };

    load();
  }, [initialDesignId, loadCanvasJson, loadCanvasPayloadIntoCanvas]);

  const addImageFromUrl = useCallback(async (url, mediaType = 'photo') => {
    const canvas = canvasRef.current;
    if (!canvas || !url) return;

    try {
      const image = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' });
      normalizeObject(image);
      if (image.width && image.width > 540) image.scaleToWidth(540);
      image.set({ left: 540, top: 220, opacity: 1 });
      image.set('mpMediaType', mediaType);
      image.set('mpSourceUrl', url);
      canvas.add(image);
      canvas.setActiveObject(image);
      canvas.requestRenderAll();
    } catch {
      setError('Falha ao inserir imagem no canvas.');
    }
  }, []);

  const addVideoFromUrl = useCallback(async (url) => {
    if (!url) return;
    const video = document.createElement('video');
    video.src = url;
    video.crossOrigin = 'anonymous';
    video.muted = true;

    try {
      await new Promise((resolve, reject) => {
        video.addEventListener('loadeddata', resolve, { once: true });
        video.addEventListener('error', reject, { once: true });
      });

      const frameCanvas = document.createElement('canvas');
      frameCanvas.width = video.videoWidth || 1280;
      frameCanvas.height = video.videoHeight || 720;
      frameCanvas.getContext('2d')?.drawImage(video, 0, 0, frameCanvas.width, frameCanvas.height);
      await addImageFromUrl(frameCanvas.toDataURL('image/png'), 'video');
      setVideoSourceUrl(url);

      const active = canvasRef.current?.getActiveObject();
      if (active) active.set('mpSourceUrl', url);
    } catch {
      setError('Falha ao inserir vídeo no canvas.');
    }
  }, [addImageFromUrl]);

  useEffect(() => {
    if (!initialImageUrl || !canvasRef.current) return;
    addImageFromUrl(initialImageUrl, 'photo');
  }, [addImageFromUrl, initialImageUrl]);

  useEffect(() => {
    if (!initialDesignPayload || !canvasRef.current) return;
    loadCanvasPayloadIntoCanvas(initialDesignPayload);
  }, [initialDesignPayload, loadCanvasPayloadIntoCanvas]);

  const addText = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const text = new Textbox('Novo texto', {
      left: 120,
      top: 160,
      width: 440,
      fontSize: textStyle.size,
      fill: themeColors.fg,
      fontFamily: textStyle.family,
      fontWeight: textStyle.weight,
      charSpacing: textStyle.spacing,
    });
    normalizeObject(text);
    canvas.add(text);
    canvas.setActiveObject(text);
    canvas.requestRenderAll();
  }, [textStyle.family, textStyle.size, textStyle.spacing, textStyle.weight, themeColors.fg]);

  const addShape = useCallback((shape) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const common = { left: 180, top: 220, fill: themeColors.accent, opacity: 0.85 };
    let object = null;

    if (shape === 'circle') object = new Circle({ ...common, radius: 120 });
    if (shape === 'triangle') object = new Triangle({ ...common, width: 240, height: 220 });
    if (!object) object = new Rect({ ...common, width: 260, height: 160, rx: 18, ry: 18 });

    normalizeObject(object);
    canvas.add(object);
    canvas.setActiveObject(object);
    canvas.requestRenderAll();
  }, [themeColors.accent]);

  const applyTextStyle = useCallback((nextStyle) => {
    setTextStyle((previous) => {
      const merged = { ...previous, ...nextStyle };
      if (merged.preview_url) loadFontCss(merged.preview_url);

      const canvas = canvasRef.current;
      const active = canvas?.getActiveObject();
      if (canvas && active && (active.type === 'textbox' || active.type === 'text')) {
        active.set({
          fontFamily: merged.family,
          fontWeight: String(merged.weight),
          fontSize: Number(merged.size),
          charSpacing: Number(merged.spacing),
        });
        canvas.requestRenderAll();
        scheduleAutoSave();
      }

      return merged;
    });
  }, [loadFontCss, scheduleAutoSave]);

  const setSelectedOpacity = useCallback((value) => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;

    const normalized = Math.max(0, Math.min(100, Number(value || 0)));
    active.set('opacity', normalized / 100);
    canvas.requestRenderAll();
    setOpacity(normalized);
    scheduleAutoSave();
  }, [scheduleAutoSave]);

  const bringForward = useCallback(() => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    canvas.bringObjectForward(active);
    canvas.requestRenderAll();
  }, []);

  const sendBackward = useCallback(() => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    canvas.sendObjectBackwards(active);
    canvas.requestRenderAll();
  }, []);

  const rotateSelected = useCallback((delta = 15) => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    active.rotate((active.angle || 0) + delta);
    canvas.requestRenderAll();
  }, []);

  const removeSelected = useCallback(() => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active) return;
    canvas.remove(active);
    canvas.requestRenderAll();
    refreshLayers();
  }, [refreshLayers]);

  const selectLayer = useCallback((layerId) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const object = canvas.getObjects().find((item) => item.get('mpObjectId') === layerId);
    if (!object) return;
    canvas.setActiveObject(object);
    canvas.requestRenderAll();
    refreshLayers();
  }, [refreshLayers]);

  const uploadImage = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      await addImageFromUrl(String(reader.result || ''), 'photo');
    };
    reader.readAsDataURL(file);
  }, [addImageFromUrl]);

  const addMarketingLogo = useCallback(async () => addImageFromUrl('/logo-colorida.png', 'photo'), [addImageFromUrl]);
  const addDefaultLogo = useCallback(async () => addImageFromUrl('/logo-branca.png', 'photo'), [addImageFromUrl]);

  const removeBackgroundSelected = useCallback(async () => {
    const canvas = canvasRef.current;
    const active = canvas?.getActiveObject();
    if (!canvas || !active || active.type !== 'image') return;

    try {
      setError('');
      const sourceUrl = active.get('mpSourceUrl');
      let result = null;

      if (typeof sourceUrl === 'string' && sourceUrl.startsWith('http')) {
        result = await removeBgService.fromUrl(sourceUrl);
      } else {
        const temp = document.createElement('canvas');
        temp.width = Math.round((active.width || 512) * (active.scaleX || 1));
        temp.height = Math.round((active.height || 512) * (active.scaleY || 1));
        const ctx = temp.getContext('2d');
        const element = active.getElement?.() || active._element;
        if (ctx && element) {
          ctx.drawImage(element, 0, 0, temp.width, temp.height);
        }
        result = await removeBgService.fromDataUrl(temp.toDataURL('image/png'));
      }

      if (!result?.success || !result?.image_data_url) {
        setError(result?.message || 'Não foi possível remover o fundo da imagem.');
        return;
      }

      const replacement = await FabricImage.fromURL(result.image_data_url, { crossOrigin: 'anonymous' });
      normalizeObject(replacement);
      replacement.set({
        left: active.left,
        top: active.top,
        angle: active.angle,
        opacity: active.opacity,
        scaleX: active.scaleX,
        scaleY: active.scaleY,
      });
      replacement.set('mpMediaType', active.get('mpMediaType') || 'photo');
      replacement.set('mpSourceUrl', result.image_data_url);

      canvas.remove(active);
      canvas.add(replacement);
      canvas.setActiveObject(replacement);
      canvas.requestRenderAll();
      scheduleAutoSave();
    } catch {
      setError('Falha ao remover o fundo da imagem selecionada.');
    }
  }, [scheduleAutoSave]);

  const exportPng = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (document.fonts?.ready) await document.fonts.ready;
    await waitForCanvasImages();
    const png = canvas.toDataURL({ format: 'png', multiplier: 2 });

    const anchor = document.createElement('a');
    anchor.href = png;
    anchor.download = `marketingpro-design-${Date.now()}.png`;
    anchor.click();
  }, [waitForCanvasImages]);

  const downloadVideo = useCallback(() => {
    if (!videoSourceUrl) return;
    const anchor = document.createElement('a');
    anchor.href = videoSourceUrl;
    anchor.target = '_blank';
    anchor.rel = 'noreferrer';
    anchor.click();
  }, [videoSourceUrl]);

  return {
    canvasElementRef,
    isReady,
    isLoading,
    isSaving,
    error,
    designId,
    designTitle,
    setDesignTitle,
    layers,
    activeLayerId,
    activeObjectType,
    textStyle,
    opacity,
    videoSourceUrl,
    addText,
    addShape,
    applyTextStyle,
    setSelectedOpacity,
    addImageFromUrl,
    addVideoFromUrl,
    uploadImage,
    addMarketingLogo,
    addDefaultLogo,
    removeBackgroundSelected,
    bringForward,
    sendBackward,
    rotateSelected,
    removeSelected,
    selectLayer,
    saveCanvas,
    exportPng,
    downloadVideo,
  };
}
